type WebStorageArea = "localStorage" | "sessionStorage";

export interface SafeStorage {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
    removeItem: (key: string) => void;
}

// Safari exposes `window.localStorage` / `window.sessionStorage` as null (or
// throws on access) when storage is blocked or partitioned, which happens in
// the OAuth callback flow. Every access has to tolerate a missing area, and
// writes also have to tolerate a full quota in private browsing.
const readStorage = (
    area: WebStorageArea,
    read: (storage: Storage) => string | null,
): string | null => {
    try {
        const storage: Storage | null = window[area];
        return storage ? read(storage) : null;
    } catch {
        return null;
    }
};

const mutateStorage = (
    area: WebStorageArea,
    mutate: (storage: Storage) => void,
): void => {
    try {
        const storage: Storage | null = window[area];
        if (storage) {
            mutate(storage);
        }
    } catch {
        return;
    }
};

const createSafeStorage = (area: WebStorageArea): SafeStorage => ({
    getItem: (key) => readStorage(area, (storage) => storage.getItem(key)),
    setItem: (key, value) =>
        mutateStorage(area, (storage) => storage.setItem(key, value)),
    removeItem: (key) =>
        mutateStorage(area, (storage) => storage.removeItem(key)),
});

export const safeLocalStorage = createSafeStorage("localStorage");
export const safeSessionStorage = createSafeStorage("sessionStorage");
